// note: general import
import * as React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer, Zoom } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// note: state import
import { selectAuth, authAction, selectLoginInput } from "state/reducer/auth";

// note MUI import
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";

import { MCSnackbarsBV2 } from "components/Reusable/Snackbar";
import { WhatIsBimDialogVB1 } from "components/Reusable/Dialog";

// note: utils import
import { BimaLogoNew, TenantManagement } from "utils/asset";

import { LazyLoadImage } from "react-lazy-load-image-component";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";

import {
  MailOutline,
  LockOutlined,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import Grid from "@mui/material/Grid2";
import {
  IconButton,
  InputAdornment,
  Link,
  Paper,
  TextField,
} from "@mui/material";
import { setAccessCookie } from "utils/cookies";
import { userAction } from "state/reducer/user";
import { login } from "libs/api/auth";
import { useTheme } from "@mui/material/styles";
import { useCASL } from "contexts/CASLContext";

function LoginPage() {
  const theme = useTheme();
  const { fetchUserAndAbilities } = useCASL();

  const [showPassword, setShowPassword] = React.useState(false);
  const [isSSOLoading, setIsSSOLoading] = React.useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // note: declare state
  const loginState = useSelector(selectAuth).login;

  // note: declare states
  const bodyLoginInput = useSelector(selectLoginInput);
  // const login = useSelector(selectAuth)?.login;

  function handleCloseNotification(_, reason) {
    if (reason === "clickaway") {
      return;
    }

    dispatch(authAction.setClearLogin());
  }

  function handleLoginInput(event) {
    const { name, value } = event.target;
    dispatch(
      authAction.setLoginInput({
        login_input: {
          ...bodyLoginInput,
          [name]: value,
        },
      }),
    );
  }

  // note: login fulfilled
  const loginFulfilled = React.useCallback(
    async (response, redirectTo = "/") => {
      // expiryToken arrives as a string (ms); Number() or the cookie lib throws "option expires is invalid"
      const expiryMs = Number(response?.data?.expiryToken) || 3600000;

      Promise.allSettled([
        // note: set access cookie
        setAccessCookie(
          "token",
          JSON.stringify({
            token: response.data.token,
          }),
          {
            expires: new Date(Date.now() + expiryMs), // note: uses expires
            domain: process.env.REACT_APP_COOKIE_DOMAIN,
          },
        ),

        // note: get profile
        dispatch(
          userAction.getProfileThunk({
            pathname: "/user/profile",
          }),
        ),

        fetchUserAndAbilities(),
      ])
        .then(() => {
          navigate(redirectTo, { replace: true });
        })
        .catch((error) => {
          console.error(error);
        });
    },
    [dispatch, navigate],
  );

  // note: handle login submit
  const handleLoginSubmit = React.useCallback(
    async (event) => {
      event.preventDefault();

      // Tampilkan loading toast
      const loadingToast = toast.loading("Sedang login...", {
        position: "bottom-right",
        theme: theme?.palette?.mode || "light",
      });

      try {
        const loginResponse = await login({
          pathname: "/login",
          body: bodyLoginInput,
        });

        // Update loading toast menjadi sukses
        toast.update(loadingToast, {
          render: loginResponse?.data?.message || "Login berhasil!",
          type: "success",
          isLoading: false,
          position: "bottom-right",
          autoClose: 3000,
          theme: theme?.palette?.mode || "light",
          transition: Zoom,
        });
        // setAccessCookie("token", loginResponse?.data?.token);

        await loginFulfilled(loginResponse);
      } catch (error) {
        console.error("Login error:", error);

        // Update loading toast menjadi error
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Login gagal. Silakan coba lagi.";
        toast.update(loadingToast, {
          render: errorMessage,
          type: "error",
          isLoading: false,
          position: "bottom-right",
          autoClose: 5000,
          theme: theme?.palette?.mode || "light",
          transition: Zoom,
        });
      }
    },
    [bodyLoginInput, theme, loginFulfilled],
  );

  // note: SSO callback, SSO server redirects back to /login?redirect=...&data=<jwt> (or &error=...)
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoData = params.get("data");
    const ssoError = params.get("error");
    const redirectTo = params.get("redirect") || "/";

    if (!ssoData && !ssoError) return;

    // note: drop data/error from the url so a refresh doesn't replay it
    navigate("/login", { replace: true });

    if (ssoError) {
      toast.error(`SSO Sign in error: ${ssoError}`);
      return;
    }

    setIsSSOLoading(true);
    login({ pathname: "/login-sso", body: { data: ssoData } })
      .then((response) => {
        toast.success(response?.data?.message || "Login berhasil!");
        return loginFulfilled(response, redirectTo);
      })
      .catch((error) => {
        // note: 404 = email not in db; prod masks messages, so the text lives here
        toast.error(
          (error?.response?.status === 404 &&
            "Akun tidak terdaftar di aplikasi, silakan hubungi departemen terkait") ||
            error?.response?.data?.message ||
            error?.message ||
            "Login SSO gagal. Silakan coba lagi.",
        );
        setIsSSOLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleLoginSSO() {
    if (isSSOLoading) return;

    const ssoUrl = process.env.REACT_APP_SSO_URL;
    if (!ssoUrl) {
      toast.error("SSO belum dikonfigurasi (REACT_APP_SSO_URL kosong).");
      return;
    }

    setIsSSOLoading(true);
    toast.info("Redirecting to Microsoft SSO...");

    const redirectTo =
      new URLSearchParams(window.location.search).get("redirect") || "/";
    const callbackUrl = `${window.location.origin}/login?redirect=${encodeURIComponent(
      redirectTo,
    )}`;

    // ponytail: no reset of isSSOLoading, the page is navigating away
    window.location.href = `${ssoUrl}?callback_url=${encodeURIComponent(callbackUrl)}`;
  }

  return (
    <Paper
      elevation={0} // Hapus bayangan
      sx={{
        minHeight: "100vh", // Pastikan tinggi minimal 100%
        height: "100%", // Isi tinggi layar
        borderRadius: 0, // Hapus sudut membulat
        overflow: "hidden", // Tetap perlukan
        fontFamily: "Manrope, sans-serif", // Pindahkan font family ke sini,
        p: 2,
      }}
    >
      {/* Set tinggi Grid container menjadi 100vh agar mengisi Paper */}
      <Grid container sx={{ height: "100vh" }}>
        {/* --- KOLOM KIRI (FORMULIR) --- */}
        <Grid
          size={{ xs: 12, md: 4.5 }}
          sx={{
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#ffffff",
            height: "100%", // Pastikan kolom mengisi tinggi
          }}
        >
          {/* Bagian Form utama */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              p: { xs: 3, sm: 5, md: 6 },
              flexGrow: 1,
              // Tambahkan overflow 'auto' jika konten lebih panjang dari layar
              overflowY: "auto",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <LazyLoadImage
                alt="logo"
                height="42px"
                // effect={effect}
                src={BimaLogoNew} // use normal <img> attributes as props
                width="42px"
                style={{
                  // border: "1px solid black",
                  // borderRadius: "10px",
                  padding: 1,
                }}
              />

              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: "#1a202c",
                }}
              >
                BIMA TENANT
              </Typography>
            </Box>

            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: "#1a202c",
                mt: 3,
                mb: 1,
              }}
            >
              Welcome to Bima Tenant
            </Typography>

            <Typography variant="body1" sx={{ color: "#718096", mb: 4 }}>
              Simplify property management with our all-in-one rental management
              system handle tenancy, payments, invoicing, contracts, revenue
              recognition.
            </Typography>

            {/* Form */}
            <Box component="form" noValidate>
              <Typography
                variant="body2"
                align="start"
                sx={{ mt: 2, color: "#718096" }}
              >
                Email Address
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                margin="dense"
                name="email"
                placeholder="hello@hypo.studio"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailOutline
                        sx={{ color: "#a0aec0", fontSize: "18px" }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "#f7fafc",
                    "& fieldset": {
                      borderColor: "#e2e8f0",
                    },
                  },
                }}
                value={bodyLoginInput.email}
                onChange={handleLoginInput}
              />

              <Typography
                variant="body2"
                align="start"
                sx={{ mt: 2, color: "#718096" }}
              >
                Password
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                margin="dense"
                placeholder="Password"
                name="password"
                type={showPassword ? "text" : "password"}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined
                        sx={{ color: "#a0aec0", fontSize: "18px" }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? (
                          <VisibilityOff sx={{ fontSize: "18px" }} />
                        ) : (
                          <Visibility sx={{ fontSize: "18px" }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "#f7fafc",
                    "& fieldset": {
                      borderColor: "#e2e8f0",
                    },
                  },
                }}
                value={bodyLoginInput?.password}
                onChange={handleLoginInput}
              />

              <Typography
                variant="body2"
                align="end"
                sx={{ mt: 2, color: "#718096" }}
              >
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  sx={{ fontWeight: "bold" }}
                  onClick={() => navigate("/forgot-password")}
                >
                  Forget Password?
                </Link>
              </Typography>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.5,
                  borderRadius: "12px",
                  // backgroundColor: "#1BA3D3",
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: "bold",
                  boxShadow: "0 4px 14px 0 rgba(9, 255, 0, 0.39)",
                  "&:hover": {
                    bgcolor: "#a5f5c2",
                    color: "#166534",
                  },
                }}
                onClick={handleLoginSubmit}
              >
                Sign in
              </Button>

              <Divider sx={{ my: 3 }}>or</Divider>

              <Button
                fullWidth
                variant="outlined"
                size="large"
                // startIcon={<Google />}
                onClick={handleLoginSSO}
                disabled={isSSOLoading}
                sx={{
                  py: 1.5,
                  borderRadius: "12px",
                  borderColor: "#e2e8f0",
                  color: "#2d3748",
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: "500",
                  "&:hover": {
                    backgroundColor: "#f7fafc",
                  },
                }}
              >
                {isSSOLoading ? "Redirecting..." : "Sign in with SSO"}
              </Button>

              {/* <Typography
                variant="body2"
                align="center"
                sx={{ mt: 4, color: "#718096" }}
              >
                Don't have an account?{" "}
                <Link href="#" underline="hover" sx={{ fontWeight: "bold" }}>
                  Register
                </Link>
              </Typography> */}
            </Box>
          </Box>
        </Grid>

        {/* --- KOLOM KANAN (PROMO) --- */}
        <Grid
          size={{ xs: 12, md: 7.5 }}
          sx={{
            position: "relative",
            display: { xs: "none", md: "flex" }, // Tetap sembunyi di HP
            flexDirection: "column",
            justifyContent: "space-between",
            p: 6,
            color: "#ffffff",
            // background: "linear-gradient(160deg, #82e0ff 0%, #4a90e2 100%)",
            height: { md: "100%" }, // Pastikan kolom ini mengisi tinggi
          }}
        >
          <DotLottieReact src={TenantManagement} loop autoplay />
        </Grid>
      </Grid>

      <Box component={"section"}>
        <MCSnackbarsBV2
          open={loginState?.isRejected}
          severity={(loginState?.isRejected && "error") || ""}
          handleClose={handleCloseNotification}
          message={loginState?.err}
          autoHideDuration={3000}
        />
        <WhatIsBimDialogVB1 />
      </Box>

      {/* ToastContainer untuk react-toastify */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </Paper>
  );
}

export default LoginPage;
